import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Profile } from "@types";

import { NavItem } from "@components/Profile/ProfileNav";

interface ProfileNavMobileProps {
  profile: Profile;
  user?: any;
  dropdownOptions?: any;
  selectedNav?: string;
  items?: NavItem[];
  handleNavChange?: (value: string) => void;
  readOnly?: boolean;
}

const ProfileNavMobile = ({
  profile,
  items,
  handleNavChange,
  selectedNav,
}: ProfileNavMobileProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Skip if clicking menu items
      const target = event.target as Element;
      if (target.closest(".menu-item-button")) {
        return;
      }

      if (
        isMenuOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Update the menu position when hamburger button is clicked
  const handleToggleMenu = () => {
    if (!isMenuOpen && hamburgerRef.current) {
      const rect = hamburgerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      });
    }
    setIsMenuOpen(!isMenuOpen);
  };

  // Find active item
  const activeItem =
    items?.find((item) => item.value === selectedNav) || items?.[0];

  // Menu component using portal
  const MenuDropdown = () => {
    if (!isMenuOpen) return null;

    // Handle menu item click
    const handleMenuItemClick = (itemValue: string) => {
      // Close menu first
      setIsMenuOpen(false);

      // Navigate using the provided handler
      if (handleNavChange) {
        setTimeout(() => {
          handleNavChange(itemValue);
        }, 10);
      }
    };

    return createPortal(
      <div
        style={{
          position: "fixed",
          top: `${menuPosition.top}px`,
          right: `${menuPosition.right}px`,
          zIndex: 1000,
        }}
        className="bg-white border border-highlight_red rounded-lg shadow-xl w-[200px]"
      >
        <div className="py-2">
          {items?.map((item, index) => {
            const isActive = selectedNav === item.value;

            return (
              <button
                key={`${item.value}-${index}`}
                className={`menu-item-button w-full text-left px-4 py-3 ${
                  isActive
                    ? "bg-highlight_red text-black font-medium"
                    : "text-neutral_600"
                }`}
                onClick={() => handleMenuItemClick(item.value)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="border-b border-highlight_red w-full" ref={containerRef}>
      <div className="flex items-center justify-between py-3 pl-6 pr-3">
        <h1 className="text-lg font-medium text-dark_night_sky">
          {activeItem?.label || profile.first_name + " " + profile.last_name}
        </h1>
        <button
          ref={hamburgerRef}
          onClick={handleToggleMenu}
          className="flex items-center justify-center h-[40px] w-[40px] rounded-lg hover:bg-highlight_red transition-colors"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col gap-[4px]">
            <div className="bg-dark_night_sky w-[14px] h-[1px]"></div>
            <div className="bg-dark_night_sky w-[14px] h-[1px]"></div>
            <div className="bg-dark_night_sky w-[14px] h-[1px]"></div>
          </div>
        </button>
      </div>
      <MenuDropdown />
    </div>
  );
};

export default ProfileNavMobile;
